import * as Sentry from "@sentry/react";
import { apiPost } from "../_apiShim";

/**
 * In-app support client: file a bug report or escalate the AI conversation to a
 * human. Posts to the backend support surface (the user's own session) which
 * lands it in Crisp; the caller's IDENTITY is stamped server-side from the JWT,
 * so this only sends DIAGNOSTIC context (page, app version, error id, …).
 *
 * Vendor-agnostic on purpose — the FE never talks to Crisp's REST API directly
 * (that needs server secrets); the live-chat WIDGET is the only client-side
 * Crisp surface (see lib/integrations/crisp.ts).
 */

const BASE_PATH = "/api/v1/support";

/** Scope/conversation hints the chat already knows; merged into the diagnostic context. */
export type SupportContext = {
  eventId?: string | null;
  conversationId?: string | null;
  surface?: string | null;
  role?: string | null;
};

/** origin + pathname only — NEVER the query string (magic-link tokens, invite
 *  tokens, filter params can ride in `?…` and must not leak into a 3rd-party
 *  ticket). */
function safePageUrl(): string {
  if (typeof window === "undefined") return "";
  const { origin, pathname } = window.location;
  return `${origin}${pathname}`;
}

function gatherContext(extra: SupportContext): Record<string, string> {
  const ctx: Record<string, string> = {
    page: safePageUrl(),
    app_version: (import.meta.env.VITE_BUILD_VERSION as string | undefined) ?? "dev",
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    locale: typeof navigator !== "undefined" ? navigator.language : "",
  };
  const sentryId = Sentry.lastEventId();
  if (sentryId) ctx.sentry_event_id = sentryId;
  if (extra.eventId) ctx.event_id = extra.eventId;
  if (extra.conversationId) ctx.conversation_id = extra.conversationId;
  if (extra.surface) ctx.surface = extra.surface;
  if (extra.role) ctx.role = extra.role;
  return ctx;
}

/**
 * Files a bug report. Also captures a Sentry user-feedback bound to the user's
 * most recent error event (associatedEventId) so engineers see the report next
 * to the crash. The Sentry call is best-effort — never blocks the submit.
 *
 * 2026-08-14 (IT-29): the reporter's name and email no longer ride along. The
 * feedback envelope does NOT pass through `beforeSend`, so those two fields
 * were the one PII channel into error monitoring that the scrub could not
 * reach — and our security papers state in four places that no personal data
 * goes there. The identity is not lost: the Crisp ticket below is stamped
 * server-side from the session, and `associatedEventId` still ties the report
 * to the crash. Do not re-add `name`/`email` here without changing the papers.
 */
export async function reportBug(input: {
  message: string;
  severity?: string;
  context: SupportContext;
}): Promise<void> {
  const context = gatherContext(input.context);
  try {
    Sentry.captureFeedback({
      message: input.message,
      associatedEventId: Sentry.lastEventId(),
    });
  } catch {
    // Sentry disabled / not initialised — ignore, the ticket still goes through.
  }
  await apiPost<{ ok: boolean }>(`${BASE_PATH}/report`, {
    message: input.message,
    severity: input.severity,
    context,
  });
}

/** Hands the current conversation off to a human (Crisp). */
export async function escalate(input: {
  reason: string;
  summary: string;
  conversationId?: string | null;
  eventId?: string | null;
  context: SupportContext;
}): Promise<void> {
  await apiPost<{ ok: boolean }>(`${BASE_PATH}/escalate`, {
    reason: input.reason,
    summary: input.summary,
    conversationId: input.conversationId ?? undefined,
    eventId: input.eventId ?? undefined,
    context: gatherContext(input.context),
  });
}
